import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router';

import { yupResolver } from '@hookform/resolvers/yup';
import { isFunction } from 'utils';
import { mergeSchema } from 'utils/validation';

import type { IStep } from './types';

const useWizardForm = (
  steps: IStep[],
  validateOnNext: boolean,
  initialFormValues: any,
  onSubmit: (values: any, originalValues: any) => void,
  routePath: string,
  setTouchedOnMount,
) => {
  const { wizardStep } = useParams();
  const navigate = useNavigate();

  const activeStep = useMemo(
    () => (wizardStep ? steps.findIndex((step) => wizardStep === step.path.replace('/', '')) : 0),
    [wizardStep, steps],
  );

  const total = steps.length;
  const [currentStepIndex, setCurrentStepIndex] = useState(activeStep > -1 ? activeStep : 0);
  const isFirstStep: boolean = currentStepIndex === 0;
  const isLastStep: boolean = currentStepIndex >= total - 1;

  const currentStep: IStep = steps[currentStepIndex];

  const allValidationSchemas = useMemo(
    () =>
      steps
        .filter(({ validationSchema }) => validationSchema)
        .map(({ validationSchema }) => validationSchema),
    [steps],
  );

  const summarySchema = useMemo(() => mergeSchema(...allValidationSchemas), [allValidationSchemas]);

  const currentStepSchema = useMemo(
    () =>
      mergeSchema(
        ...steps
          .slice(0, currentStepIndex + 1)
          .filter(({ validationSchema }) => validationSchema)
          .map((step) => step.validationSchema),
      ),
    [steps, currentStepIndex],
  );

  const stepsInitialValues = useMemo(() => {
    return steps.reduce(
      (values, step) => (step.initialValues ? { ...values, ...step.initialValues } : values),
      {},
    );
  }, [steps]);

  const initialValues = useMemo(() => {
    return {
      ...summarySchema.cast(stepsInitialValues || {}, { assert: false }),
      ...summarySchema.cast(initialFormValues || {}, { assert: false }),
    };
  }, [initialFormValues, summarySchema, stepsInitialValues]);

  const form = useForm({
    mode: 'onChange',
    defaultValues: initialValues,
    resolver: yupResolver(isLastStep ? summarySchema : currentStepSchema),
  });

  const { trigger, getValues, formState, handleSubmit } = form;
  const { isValid, errors } = formState;
  const values = getValues();
  const firstInvalidStepIndex: number = useMemo(() => {
    let i = -1;
    for (const step of steps) {
      i++;
      if (!step.validationSchema) continue;
      try {
        step.validationSchema.validateSync(values);
      } catch (e) {
        return i;
      }
    }

    return -1;
  }, [steps, values]);

  useEffect(() => {
    if (activeStep === currentStepIndex) trigger();
  }, [activeStep, currentStepIndex, trigger]);

  // Set touched fields with errors - React Hook Form handles this automatically
  useEffect(() => {
    if (setTouchedOnMount && Object.keys(errors).length > 0) {
      // React Hook Form automatically marks fields as touched on interaction
      // We can trigger validation to show all errors immediately
      trigger();
    }
  }, [setTouchedOnMount, trigger, errors]);

  // Set current step based on the route
  useEffect(() => {
    if (activeStep !== currentStepIndex && activeStep !== -1) {
      setCurrentStepIndex(activeStep);
    }
  }, [activeStep, currentStepIndex]);

  // Redirect to the first step if the wizardStep doesn't exist
  useEffect(() => {
    if (activeStep === -1) {
      navigate(routePath);
    }
  }, [activeStep, navigate, routePath]);

  // Redirect to the invalid step if the wizardStep is different as the current one
  useEffect(() => {
    if (
      !isValid &&
      firstInvalidStepIndex >= 0 &&
      firstInvalidStepIndex !== currentStepIndex &&
      firstInvalidStepIndex < currentStepIndex
    ) {
      const { path = '' } = steps[firstInvalidStepIndex];
      navigate(`${routePath}${path}`);
    }
  }, [firstInvalidStepIndex, isValid, errors, steps, navigate, routePath, currentStepIndex]);

  const isNextDisabled: boolean = !isValid;

  const goToPrev = useCallback(() => {
    const nextIndex = Math.max(0, currentStepIndex - 1);

    const { path } = steps[nextIndex];
    navigate(routePath + path);
  }, [currentStepIndex, navigate, steps, routePath]);

  const goToNext = useCallback(() => {
    const nextIndex = Math.min(currentStepIndex + 1, total - 1);

    const { path } = steps[nextIndex];
    navigate(routePath + path);
  }, [currentStepIndex, total, navigate, steps, routePath]);

  const { beforePrev, beforeNext } = currentStep;

  const handlePrev = useCallback(async () => {
    let isValid = true;

    if (isFunction(beforePrev)) {
      try {
        await beforePrev!(values, form, currentStepIndex);
      } catch (error) {
        isValid = false;
      }
    }

    if (isValid) {
      goToPrev();
    }
  }, [goToPrev, currentStepIndex, beforePrev, values, form]);

  const handleNext = useCallback(async () => {
    let isValidStep = false;

    if (validateOnNext) {
      isValidStep = formState.isValid;
    }

    if (((validateOnNext && isValidStep) || !validateOnNext) && isFunction(beforeNext)) {
      try {
        await beforeNext!(values, form, currentStepIndex);
        isValidStep = true;
      } catch (error) {
        isValidStep = false;
      }
    }

    if (!isValidStep) return;

    if (isLastStep) {
      await handleSubmit((data) => {
        onSubmit(
          summarySchema.cast(data, {
            stripUnknown: true,
            assert: false,
          }),
          data,
        );
      })();
    } else {
      goToNext();
    }
  }, [
    goToNext,
    currentStepIndex,
    beforeNext,
    isLastStep,
    validateOnNext,
    values,
    form,
    formState.isValid,
    handleSubmit,
    onSubmit,
    summarySchema,
  ]);

  return {
    form,
    currentStep,
    isNextDisabled,
    isFirstStep,
    isLastStep,
    goToPrev,
    goToNext,
    handlePrev,
    handleNext,
  };
};

export { useWizardForm as useWizard };
